import { FaPencilAlt, FaShareAlt, FaStar, FaRegStar } from "react-icons/fa";
import { SUBJECT_OPTIONS, getPastExamLabel, formatDuration } from "../constants/answerMetadata";
import { ChevronDownIcon } from "@chakra-ui/icons";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Tooltip,
  IconButton,
  Flex,
  Button,
  ListItem,
  Text,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Switch,
  useClipboard,
  useToast,
  Badge,
} from "@chakra-ui/react";
import {
  updateAnswerTitle,
  deleteAnswer,
  getPublicAnswerShareUrl,
  togglePublicAccess,
  updateAnswerFavorite,
} from "../api";
import { exportAnswerToWord } from "../utils/answerWord";
import {
  exportAnswerToPdf,
  printAnswerInWordLayout,
  printAnswerPdf,
} from "../utils/answerPdf";
import {
  getAnswerTemplateLabel,
  normalizeAnswerTemplate,
} from "../constants/answerTemplates";

const getAnswerHtml = (answer) => {
  if (answer.content_html?.trim()) return answer.content_html;

  const container = document.createElement("div");
  String(answer.content || "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .forEach((line) => {
      const block = document.createElement("div");
      if (line) {
        block.textContent = line;
      } else {
        block.appendChild(document.createElement("br"));
      }
      container.appendChild(block);
    });

  return container.innerHTML;
};

const AnswerListItem = ({ answer, answers, setAnswers, onListChanged }) => {
  const [favoritePending, setFavoritePending] = useState(false);
  const [favorite, setFavorite] = useState(Boolean(answer.is_favorite));
  const [isPublic, setIsPublic] = useState(answer.is_public);
  const shareUrl = getPublicAnswerShareUrl(answer.answer_id);
  const { hasCopied, onCopy } = useClipboard(shareUrl);
  const [editingId, setEditingId] = useState(null); // Track the answer being edited
  const [newTitle, setNewTitle] = useState(""); // Store the updated title temporarily
  const [exportingOption, setExportingOption] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();

  const handleFavorite = async () => {
    if (favoritePending) return;
    const next = !favorite;
    setFavorite(next);
    setFavoritePending(true);
    try {
      await updateAnswerFavorite(answer.answer_id, next);
      setAnswers((current) => current.map((item) => item.answer_id === answer.answer_id ? { ...item, is_favorite: next } : item));
      onListChanged?.();
    } catch (error) {
      setFavorite(!next);
      toast({ title: "즐겨찾기를 변경하지 못했습니다.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setFavoritePending(false);
    }
  };

  const handleEditTitle = (answerId, currentTitle) => {
    setEditingId(answerId); // Set the current answer as being edited
    setNewTitle(currentTitle); // Set the current title in the input field
  };

  const handleTitleChange = (event) => {
    setNewTitle(event.target.value); // Update the title as the user types
  };

  const handleViewAnswer = (id) => {
    navigate(`/answers/${id}`);
  };

  const handleDeleteAnswer = async (answer_id) => {
    const isConfirmed = window.confirm("정말로 이 답안지를 삭제하시겠습니까?");

    if (!isConfirmed) {
      return; // 사용자가 취소하면 함수 종료
    }
    try {
      await deleteAnswer(answer_id); // Call the delete function
      setAnswers(answers.filter((answer) => answer.answer_id !== answer_id)); // Remove the answer from the state after successful deletion
    } catch (error) {
      console.error("Failed to delete the answer:", error);
      alert("답안지를 삭제하는 데 실패했습니다.");
    }
  };

  const handleSaveTitle = async (answerId) => {
    try {
      // Call an API function to update the answer title (You can define this function in api.js)
      await updateAnswerTitle(answerId, newTitle);

      // Update the local state with the new title
      setAnswers((prevAnswers) =>
        prevAnswers.map((answer) =>
          answer.answer_id === answerId
            ? { ...answer, subject: newTitle }
            : answer,
        ),
      );
      onListChanged?.();
    } catch (error) {
      console.error("Failed to update title:", error);
    }
    setEditingId(null); // Exit edit mode
  };

  const handleTogglePublicAccess = async () => {
    try {
      const response = await togglePublicAccess(answer.answer_id, isPublic);

      if (response.ok) {
        setIsPublic(!isPublic); // Update the local state to reflect the new public status
      } else {
        console.error("Failed to update public access");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleShare = async () => {
    if (!isPublic) return;

    const shareData = {
      title: `${answer.subject} 답안지`,
      text: `${answer.nickname || "수험생"}님의 답안지를 확인해보세요.`,
      url: shareUrl,
    };

    if (typeof navigator.share === "function") {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error("Failed to open the share sheet:", error);
      }
    }

    onCopy();
    toast({
      title: "공유 링크를 복사했습니다.",
      description: "카카오톡 채팅방에 붙여넣으면 답안지 미리보기가 표시됩니다.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const handleExport = async (option) => {
    if (exportingOption) return;

    const payload = {
      html: getAnswerHtml(answer),
      subject: answer.subject,
      templateType: normalizeAnswerTemplate(answer.template_type),
    };
    const labels = {
      pdfSave: "PDF 저장",
      pdfPrint: "PDF 인쇄",
      wordSave: "Word 저장",
    };

    setExportingOption(option);
    try {
      if (option === "pdfSave") await exportAnswerToPdf(payload);
      if (option === "pdfPrint") await printAnswerPdf(payload);
      if (option === "wordSave") await exportAnswerToWord(payload);
      if (option === "wordPrint") await printAnswerInWordLayout(payload);

      toast({
        title: `${labels[option]} 준비가 완료되었습니다.`,
        description:
          option === "wordPrint"
            ? "Word 저장과 같은 답안지 양식으로 인쇄 창을 열었습니다."
            : undefined,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(`${labels[option]} 실패:`, error);
      toast({
        title: `${labels[option]}에 실패했습니다.`,
        description: error.message || "잠시 후 다시 시도해주세요.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExportingOption(null);
    }
  };

  return (
    <ListItem
      key={answer.answer_id}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      p="2"
      borderBottom="1px solid gray"
      width="full"
      gap="2"
      flexWrap={{ base: "wrap", lg: "nowrap" }}
    >
      <IconButton aria-label={favorite ? "즐겨찾기 해제" : "즐겨찾기 등록"} aria-pressed={favorite} icon={favorite ? <FaStar /> : <FaRegStar />} color={favorite ? "yellow.500" : "gray.500"} size="sm" variant="ghost" isDisabled={favoritePending} onClick={handleFavorite} />
      <Flex alignItems="center" flex="2" minW="150px">
        {editingId === answer.answer_id ? (
          // If this answer is being edited, show an input field
          <Input
            value={newTitle}
            onChange={handleTitleChange}
            onBlur={() => handleSaveTitle(answer.answer_id)} // Save when input loses focus
            autoFocus
            size="sm"
          />
        ) : (
          <>
            <Flex direction="column" align="flex-start">
              <Text overflowWrap="anywhere">{answer.subject}</Text>
              {answer.subject_code && <Text fontSize="xs" color="gray.600">{SUBJECT_OPTIONS.find(([value]) => value === answer.subject_code)?.[1]}</Text>}
              {getPastExamLabel(answer) && <Text fontSize="xs" color="gray.600">{getPastExamLabel(answer)}</Text>}
              <Badge colorScheme="blue" fontSize="9px" mt="1">
                {getAnswerTemplateLabel(answer.template_type)}
              </Badge>
            </Flex>
            <Tooltip label="답안 제목 수정" aria-label="Edit title tooltip">
              <IconButton
                aria-label="Edit title"
                icon={<FaPencilAlt />}
                size="sm"
                ml="2"
                variant="ghost" // Optional: set button style
                onClick={() =>
                  handleEditTitle(answer.answer_id, answer.subject)
                } // Enter edit mode
              />
            </Tooltip>
          </>
        )}
      </Flex>
      <Flex direction="column" color="gray.500" fontSize="11px" flex="1.3" minW="120px">
        <Text>{formatDate(answer.created_at || answer.modified_at)}</Text>
        {answer.duration_seconds != null && <Text>작성시간 {formatDuration(answer.duration_seconds)}</Text>}
      </Flex>
      <Button
        size="xs"
        background="blue.600"
        color={"white"}
        onClick={() => handleViewAnswer(answer.answer_id)}
      >
        보기
      </Button>
      <Flex alignItems="center">
        <Switch isChecked={isPublic} onChange={handleTogglePublicAccess} />
      </Flex>

      <Button
        onClick={handleShare}
        background="white"
        color={"blue.600"}
        ml="4"
        variant="ghost"
        isDisabled={!isPublic}
        size="sm"
        leftIcon={<FaShareAlt />}
        aria-label="답안지 공유"
        title={
          isPublic
            ? "모바일에서는 카카오톡을 선택할 수 있습니다."
            : "먼저 답안지를 공개로 설정해주세요."
        }
      >
        {hasCopied ? "복사됨" : "공유"}
      </Button>

      <Menu placement="bottom-end">
        <MenuButton
          as={Button}
          size="sm"
          colorScheme="teal"
          variant="outline"
          rightIcon={<ChevronDownIcon />}
          isLoading={Boolean(exportingOption)}
          loadingText="준비 중"
        >
          내보내기
        </MenuButton>
        <MenuList zIndex="popover">
          <MenuItem onClick={() => handleExport("pdfSave")}>PDF 저장</MenuItem>
          <MenuItem onClick={() => handleExport("pdfPrint")}>PDF 인쇄</MenuItem>
          <MenuItem onClick={() => handleExport("wordSave")}>
            Word 저장
          </MenuItem>
        </MenuList>
      </Menu>

      <Button
        size="sm"
        colorScheme="red"
        onClick={() => handleDeleteAnswer(answer.answer_id)}
      >
        삭제
      </Button>
    </ListItem>
  );
};

export default AnswerListItem;
